import type { ChessMove, PromotionPiece, Square, Vec2 } from '@chessshooter/shared';

// Selecting and moving pieces as a pure state machine over squares and points, so it is tested without
// a DOM: idle → selected → (promotion) → a pending move that the mode hangs onto the next tick's input.
// Tap or drag makes no difference to the simulation (rule 7); the result is always a ChessMove.
//   - a tap on an own piece with moves selects it (and starts a drag in case the finger moves on);
//   - a tap on a target square plays the move; a target with promotions asks for the piece first;
//   - a tap on another own piece switches; a tap anywhere else, or on the selected piece again, deselects;
//   - a drag released on a target plays the move; released anywhere else, the piece snaps back and
//     stays selected (a finger that slips slightly off should not cost the selection).

/** The legal moves that start on a square: empty for an empty square, a foe's piece or a piece that cannot move. */
export type LegalMovesFrom = (from: Square) => ChessMove[];

export interface BoardInputView {
  premove?: ChessMove | null;
  selected: Square | null;
  /** target squares of the selected piece */
  targets: Square[];
  /** the piece under the finger: where it came from and where the pointer is */
  drag: { from: Square; at: Vec2 } | null;
  /** waiting for the promotion piece of this move */
  promotion: { from: Square; to: Square } | null;
}

export const EMPTY_BOARD_INPUT: BoardInputView = { selected: null, targets: [], drag: null, promotion: null };

interface Drag {
  id: number;
  at: Vec2;
  /** the piece was already selected when the pointer landed: releasing on it again deselects */
  reselect: boolean;
  /** the pointer has left the origin square, so this is no longer a tap */
  left: boolean;
}

type Mode =
  | { kind: 'idle' }
  | { kind: 'selected'; from: Square; moves: ChessMove[]; drag: Drag | null }
  | { kind: 'promotion'; from: Square; to: Square };

const IDLE: Mode = { kind: 'idle' };

export class BoardInput {
  private mode: Mode = IDLE;
  private pending: ChessMove | null = null;

  /** A pointer lands on `square` (null = off the board). `legal` says which moves start on a square. */
  pointerDown(id: number, square: Square | null, at: Vec2, legal: LegalMovesFrom): void {
    const m = this.mode;
    if (m.kind === 'promotion') {
      // a tap beside the picker cancels it; the picker's own buttons go through choosePromotion()
      this.mode = IDLE;
      return;
    }
    if (m.kind === 'selected') {
      if (m.drag) return; // a second finger while dragging
      if (square !== null && square !== m.from) {
        const moves = m.moves.filter((mv) => mv.to === square);
        if (moves.length > 0) {
          this.commit(m.from, square, moves);
          return;
        }
      }
    }
    if (square === null) {
      this.mode = IDLE;
      return;
    }
    const moves = legal(square);
    if (moves.length === 0) {
      this.mode = IDLE;
      return;
    }
    const reselect = m.kind === 'selected' && m.from === square;
    this.mode = { kind: 'selected', from: square, moves, drag: { id, at: { x: at.x, y: at.y }, reselect, left: false } };
  }

  pointerMove(id: number, square: Square | null, at: Vec2): void {
    const m = this.mode;
    if (m.kind !== 'selected' || !m.drag || m.drag.id !== id) return;
    m.drag.at = { x: at.x, y: at.y };
    if (square !== m.from) m.drag.left = true;
  }

  pointerUp(id: number, square: Square | null): void {
    const m = this.mode;
    if (m.kind !== 'selected' || !m.drag || m.drag.id !== id) return;
    const drag = m.drag;
    m.drag = null;
    if (square !== null && square !== m.from) {
      const moves = m.moves.filter((mv) => mv.to === square);
      if (moves.length > 0) this.commit(m.from, square, moves);
      return; // off a target: the piece snaps back, the selection stays
    }
    if (square === m.from && drag.reselect && !drag.left) this.mode = IDLE;
  }

  pointerCancel(id: number): void {
    const m = this.mode;
    if (m.kind === 'selected' && m.drag?.id === id) m.drag = null;
  }

  private commit(from: Square, to: Square, moves: ChessMove[]): void {
    if (moves.some((mv) => mv.promotion !== undefined)) {
      this.mode = { kind: 'promotion', from, to };
      return;
    }
    this.pending = moves[0];
    this.mode = IDLE;
  }

  choosePromotion(piece: PromotionPiece): void {
    const m = this.mode;
    if (m.kind !== 'promotion') return;
    this.pending = { from: m.from, to: m.to, promotion: piece };
    this.mode = IDLE;
  }

  /** Refresh targets after an opponent move without interrupting the held pointer. */
  refreshMoves(legal: LegalMovesFrom): void {
    if (this.mode.kind === 'selected') this.mode.moves = legal(this.mode.from);
  }

  /** Drops selection, drag and picker (the position changed under them); a pending move is kept. */
  cancel(): void {
    this.mode = IDLE;
  }

  /** Forgets everything, including a pending move: the phase ended or a new match began. */
  reset(): void {
    this.mode = IDLE;
    this.pending = null;
  }

  /** The move the player completed since the last call, consumed. */
  take(): ChessMove | undefined {
    const move = this.pending;
    this.pending = null;
    return move ?? undefined;
  }

  view(): BoardInputView {
    const m = this.mode;
    switch (m.kind) {
      case 'idle':
        return EMPTY_BOARD_INPUT;
      case 'selected':
        return {
          selected: m.from,
          targets: [...new Set(m.moves.map((mv) => mv.to))],
          drag: m.drag ? { from: m.from, at: m.drag.at } : null,
          promotion: null,
        };
      case 'promotion':
        return { selected: m.from, targets: [], drag: null, promotion: { from: m.from, to: m.to } };
    }
  }
}
